/**
 * Permissions — single source of truth for who sees Client Intel,
 * Newsletter, and other Concierge® B2C tooling.
 *
 * Doctrine (Dipali, 30-Apr-2026):
 *   The Concierge® team — Nisha (cc-clubs leader), Samar (cc-ops leader),
 *   Shiva and Tanisha (cc-clubs sub-leaders) — needs access to Client
 *   Intel + Newsletters alongside the CEO. Other divisions (LC, ME, TDB)
 *   reach per-client data through Division → Client Dossier instead.
 *
 *   Access rule (in priority order):
 *     1. role === 'ceo'                                    → ✅
 *     2. role ∈ {leader, sub_leader} AND division starts   → ✅
 *        with 'cc-' (i.e. cc-clubs, cc-ops, future cc-*)
 *     3. email in CONCIERGE_TEAM_EMAILS allowlist          → ✅ (backup)
 *
 *   Rule 2 is the durable fix — adding a new sub-leader to cc-clubs/cc-ops
 *   automatically gives them access without touching this file.
 */

const CONCIERGE_TEAM_EMAILS = new Set([
  'dipali@clubconcierge.in',
  'nisha@clubconcierge.in',
  'samar@clubconcierge.in',
  'shiva@clubconcierge.in',
  'tanisha@clubconcierge.in',
]);

const CONCIERGE_DIVISION_PREFIX = 'cc-';
const PRIVILEGED_ROLES = new Set(['leader', 'sub_leader']);

export function canAccessClientIntel(user) {
  if (!user) return false;
  const role = (user.role || '').toLowerCase();
  if (role === 'ceo') return true;
  // Rule 2 — role + division (durable, no email list needed).
  if (PRIVILEGED_ROLES.has(role)
      && (user.division_id || '').toLowerCase().startsWith(CONCIERGE_DIVISION_PREFIX)) {
    return true;
  }
  // Rule 3 — email allowlist (back-up for users in non-standard divisions).
  return CONCIERGE_TEAM_EMAILS.has((user.email || '').toLowerCase());
}

export const canAccessNewsletter = canAccessClientIntel;


/**
 * User Scopes (introduced 11-May-2026).
 *
 * Some users need a tightly restricted view — they see ONLY the tabs
 * they're scoped to, nothing else. Example: Rekha (HR) → People +
 * Training + Profile + Submit Update; she should NOT see Operations,
 * Finance, Mira, etc.
 *
 * Data model:
 *   `users.scopes: string[]` — e.g. ["people", "training", "profile",
 *   "submit_update", "compliance"]
 *
 * Semantics:
 *   - If `scopes` is **non-empty**, it acts as a RESTRICTIVE WHITELIST.
 *     The user can ONLY access the tabs in the array, regardless of role.
 *   - If `scopes` is empty/null/missing, fall back to role-based access
 *     (everyone gets the default for their role — backward compatible).
 *   - CEO is exempt: scopes are ignored for CEO accounts.
 *
 * Valid scope tokens (keep in sync with Layout.js NAV_SCOPE_MAP):
 *   - "people"          — People page + KYC docs + person profile
 *   - "training"        — Training/MyTraining
 *   - "profile"         — Own profile page
 *   - "submit_update"   — WhatsApp/Submit Update form
 *   - "compliance"      — Compliance docs page
 *   - "outlets"         — Outlet Intel
 *   - "client_intel"    — Client Intelligence + Newsletter
 *   - "workbench"       — Member Care Workbench
 *   - "operations"      — Operations dashboard
 *   - "finance"         — Finance / Targets / Pace
 *   - "mira"            — Operator Mira admin panel
 *   - "users"           — User management (admin/users tab)
 */
export function isScopeRestricted(user) {
  if (!user) return false;
  if ((user.role || '').toLowerCase() === 'ceo') return false;
  const scopes = user.scopes || [];
  return Array.isArray(scopes) && scopes.length > 0;
}

export function userHasScope(user, scopeToken) {
  if (!user) return false;
  if ((user.role || '').toLowerCase() === 'ceo') return true;
  const scopes = user.scopes || [];
  if (!Array.isArray(scopes) || scopes.length === 0) {
    // Unrestricted user — defer to role-based defaults (caller decides).
    return null;
  }
  return scopes.includes(scopeToken);
}


/**
 * HR head (Jun-2026 · Sneha / Nandini · MindEscapes).
 * Scoped account with `people` scope AND category "hr". Mirrors backend
 * `auth.is_hr_head` — full HR tier within own division.
 */
export function isHrHead(user) {
  if (!user || (user.role || '').toLowerCase() === 'ceo') return false;
  const scopes = user.scopes || [];
  return Array.isArray(scopes) && scopes.includes('people') && (user.category || '').toLowerCase() === 'hr';
}

/** Scope-restricted user whose home is the HR Hub (has `people`, lacks `division`). */
export function isHrHomeUser(user) {
  if (!isScopeRestricted(user)) return false;
  const scopes = user.scopes || [];
  return scopes.includes('people') && !scopes.includes('division');
}

export function canManageUsers(user) {
  if (!user) return false;
  if ((user.role || '').toLowerCase() === 'ceo') return true;
  return isHrHead(user) && (user.scopes || []).includes('users');
}
